import ErrorBoundary from "@components/error-boundary";
import { useAuthStoreShallow } from "@/features/authentication";
import {
  DateVariablePopover,
  isDateVariableName,
  isNowBasedDateName,
  parseDateToken,
  replacePillTokenAt,
} from "@features/dynamic-variables/date-variable";
import { useCalcFormulas } from "@features/dynamic-variables/calc/use-calc-formulas";
import { useCustomDateNames } from "@features/dynamic-variables/date-variable/use-custom-date-names";
import {
  VARIABLE_PILL_CLASS,
  templateToPillsHtml,
} from "@/shared/utils/variable-pill";
import { templateToBlocksHtml } from "@/shared/utils/variable-block";
import { useDocPermissions } from "@components/access/use-doc-permissions";
import { openRepeatableRowsForName } from "@features/dynamic-variables/repeatable/open-repeatable-rows-dialog";
import {
  useScopedVersionData,
  useVariablePillDecorator,
} from "@features/dynamic-variables/repeatable/use-scoped-version-data";
import { SingleVariablePopover } from "@components/ui/single-variable-popover";
import { useIsEditorContext } from "@editor-providers";
import { TextLibrary } from "@library-elements/text";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/feature-flags";
import { useAppDispatch, useAppSelector } from "@store";
import { editEntity } from "@store/story";
import {
  getComboName,
  getEntitySettings,
  getRepeatableTree,
  isEntityActive,
  isVersionInEditor,
  selectIsEntityInCell,
} from "@store/story/selectors";
import { extractCellText } from "@library-elements/table/utils/extract-cell-text";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
} from "react";
import { useOnDragEnd } from "../hooks";
import "../styles/Richtext.scss";
import { textLimitation } from "../utils/getTextLimitation";
import { Froala } from "./Froala";
import { ProseMirrorText } from "./ProseMirrorText";
import { type PillLocation, relocatePill } from "./relocate-pill";
import { trackDateVariableEdit } from "@features/dynamic-variables/analytics/variables-analytics";

export type RichTextProps = {
  defaultContent: string | null;
  content: string;
  placeholder: string | null;
  width: number;
  horizontalAlignment: string | undefined;
  animation?: any;
  uniqueKey: string;

  disableResize?: boolean;
  customOnUpdate?: (html: string) => void;
  onWrapperClick?: (e: MouseEvent<HTMLDivElement>) => void;
};

export const TextEditor = ({ id }: { id: string }) => {
  const isEditor = useIsEditorContext();
  // Send flow: a viewer/commenter never mounts Froala, so text stays read-only.
  const { canEdit } = useDocPermissions();
  const dragKey = useOnDragEnd({ id });
  const isAdmin = useAuthStoreShallow((s) => s.isAdmin);

  const settings = useAppSelector((state) =>
    getEntitySettings<RichTextProps>(id)(state),
  );

  // ProseMirror is a beta behind an org-allowlisted runtime flag; everyone
  // else keeps Froala (the branch that master ships).
  const isProseMirrorEnabled = useFeatureFlag(FEATURE_FLAGS.PROSEMIRROR_TEXT);
  const comboName = useAppSelector((state) =>
    getComboName(id)(state),
  ) as string;
  const textLimit = (textLimitation as Record<string, number>)[
    comboName ? comboName : "Simple"
  ];

  const isActive = useAppSelector((state) => isEntityActive(id)(state));
  const isVersion = useAppSelector(isVersionInEditor);
  // Repeatable groups nested by containment: a pill named `<group>.<field>`
  // edits ROWS, so it opens the rows pop-up instead of the single-value
  // popover (which would only ever edit row 1).
  const repeatableRoots = useAppSelector(getRepeatableTree);
  // An in-cell text element renders read-only until clicked; show its
  // placeholder (muted, click-through) when empty so it's visible + targetable.
  const isInCell = useAppSelector(selectIsEntityInCell(id));
  // Scoped for this element's repeatable chain (nested groups resolve against
  // ROW 1 of each parent — see useScopedVersionData); unchanged for elements
  // outside a nested chain.
  const versionData = useScopedVersionData(id);

  // Same per-pill decoration the active Froala uses, so an INACTIVE text
  // element renders a formula pill (green `--calc` variant, "calculation"
  // label, formula tooltip — Figma 80:44269 / 80:45420) and a repeatable-field
  // pill (row 1 of the scoped data) exactly like the active editor does.
  // Custom date variables are only dates by definition, not by name — same
  // set the active Froala derives, so an inactive pill resolves and opens the
  // same popover as an active one.
  const customDateNames = useCustomDateNames();
  const decorate = useVariablePillDecorator(id, versionData, customDateNames);
  const calcFormulas = useCalcFormulas();

  const displayContent = useMemo(() => {
    // Version mode resolves triple-brace blocks first (so the pill regex
    // doesn't see them), then pills with resolved values. Base editor renders
    // name-only pills; {{{blocks}}} stay raw there (no data → empty block).
    const withBlocks = isVersion
      ? templateToBlocksHtml(settings.content ?? "", versionData)
      : (settings.content ?? "");
    return templateToPillsHtml(withBlocks, versionData, decorate);
  }, [settings.content, isVersion, versionData, decorate]);

  // Pill popover state when the text element is INACTIVE (Froala isn't
  // mounted; TextLibrary renders read-only). When active, Froala owns its
  // own popover. TextLibrary calls `onPillClick` via editorData when a
  // variable pill is clicked.
  const dispatch = useAppDispatch();

  const [variablePopover, setVariablePopover] = useState<
    (PillLocation & { valueType: "text" | "date" }) | null
  >(null);

  // Date pills open the format/calculation popover while the element stays
  // inactive (no Froala) — saving rewrites the stored content token directly.
  const [datePopover, setDatePopover] = useState<
    (PillLocation & { token: string }) | null
  >(null);

  // The value popover is version-only; date pills work in authoring too, so
  // the callback must be supplied whenever the element is inactive. A
  // viewer/commenter (send flow) gets neither: both rewrite content.
  const showInactivePopover = isVersion && isEditor && !isActive && canEdit;
  const provideOnPillClick = isEditor && !isActive && canEdit;

  const pillIndexOf = useCallback((pill: HTMLElement) => {
    // Use the clicked pill's OWN rich-text container, not
    // `getElementById(rich-text-<id>)` — the thumbnails panel renders the same
    // element with a duplicate id, and getElementById would return that copy,
    // making indexOf fail (-1) and silently skip the save.
    const container = pill.closest(".rich-text-element");
    return container
      ? Array.from(
          container.querySelectorAll<HTMLElement>(`.${VARIABLE_PILL_CLASS}`),
        ).indexOf(pill)
      : -1;
  }, []);

  const handlePillClick = useCallback(
    (pill: HTMLElement, name: string): boolean => {
      const anchorIndex = pillIndexOf(pill);
      const container = pill.closest<HTMLElement>(".rich-text-element");

      // Date variable: authoring edits the token's format/offset (template
      // settings); version mode edits the sender's DATE VALUE (calendar), and
      // the built-in Time/Date group is not editable at all.
      const bareName = parseDateToken(name).name;
      if (isDateVariableName(bareName, customDateNames)) {
        if (!isVersion) {
          setDatePopover({
            anchor: pill,
            container,
            anchorIndex,
            rect: pill.getBoundingClientRect(),
            name,
            token: name,
          });
          return true;
        }
        if (isNowBasedDateName(bareName)) return true;
        setVariablePopover({
          anchor: pill,
          container,
          anchorIndex,
          rect: pill.getBoundingClientRect(),
          name: bareName,
          valueType: "date",
        });
        return true;
      }

      // Non-date pills only edit their value in version mode; otherwise let the
      // click through so the element activates for normal text editing.
      if (!isVersion) return false;

      // A calculation shows its computed result; nothing to edit.
      if (calcFormulas[name]) return true;

      // A repeatable group's field edits ROWS, not a single value — route to
      // the rows pop-up focused on the group the pill belongs to.
      if (openRepeatableRowsForName(name, repeatableRoots)) return true;

      setVariablePopover({
        anchor: pill,
        container,
        anchorIndex,
        rect: pill.getBoundingClientRect(),
        name,
        valueType: "text",
      });
      return true;
    },
    [pillIndexOf, isVersion, calcFormulas, repeatableRoots, customDateNames],
  );

  const handleDateTokenSave = useCallback(
    (nextToken: string) => {
      if (datePopover && datePopover.anchorIndex >= 0) {
        const content = settings.content ?? "";
        const next = replacePillTokenAt(
          content,
          datePopover.anchorIndex,
          nextToken,
        );
        if (next !== content) {
          dispatch(editEntity({ id, data: { content: next } }));
          trackDateVariableEdit(nextToken);
        }
      }
      setDatePopover(null);
    },
    [datePopover, dispatch, id, settings.content],
  );

  // Element became active → drop the inactive-state popovers; Froala will
  // own pill interactions from here on.
  useEffect(() => {
    if (isActive) {
      setVariablePopover(null);
      setDatePopover(null);
    }
  }, [isActive]);

  // Re-resolve the value pill after a re-render replaced its DOM node — in
  // THIS element's own render, not document-wide (see relocate-pill.ts).
  const resolveVariablePopoverAnchor = useCallback(
    () => (variablePopover ? relocatePill(variablePopover) : null),
    [variablePopover],
  );

  useEffect(() => {
    const pill = resolveVariablePopoverAnchor();
    if (!pill) return;
    pill.setAttribute("data-active", "true");
    return () => {
      pill.removeAttribute("data-active");
    };
  }, [resolveVariablePopoverAnchor]);

  // Same for the date pill (its `name` is the full token).
  const resolveDatePopoverAnchor = useCallback(
    () => (datePopover ? relocatePill(datePopover) : null),
    [datePopover],
  );

  const readOnlyText = (
    <TextLibrary
      entity={{ settings, id, children: [], type: "element" }}
      editorData={{
        isAdmin: !!isAdmin,
        width: settings.width,
        content: displayContent,
        onPillClick: provideOnPillClick ? handlePillClick : undefined,
        inheritPlaceholderTypography: isProseMirrorEnabled,
      }}
    />
  );

  if (isActive && isEditor && canEdit) {
    return isProseMirrorEnabled ? (
      // A ProseMirror crash shows the text read-only (Sentry: feature
      // "prosemirror-text") instead of taking the editor down. Leaving the
      // element and coming back mounts it again.
      <ErrorBoundary
        key={dragKey}
        feature="prosemirror-text"
        fallback={readOnlyText}
        toast="This text couldn't be opened for editing. Your text is safe. Click outside it and try again."
      >
        <ProseMirrorText
          id={id}
          pills={{
            data: versionData,
            decorate,
            isVersion,
            customDateNames,
            calcFormulas,
            repeatableRoots,
          }}
        />
      </ErrorBoundary>
    ) : (
      <Froala key={dragKey} id={id} isAdmin={isAdmin} textLimit={textLimit} />
    );
  }
  return (
    <>
      {readOnlyText}
      {isEditor &&
        isInCell &&
        !!settings.placeholder &&
        extractCellText(settings.content ?? "").trim().length === 0 && (
          <div
            className="rich-text-element"
            style={{ opacity: 0.4, pointerEvents: "none" }}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: placeholder HTML comes from the static element catalog
            dangerouslySetInnerHTML={{ __html: settings.placeholder as string }}
          />
        )}
      {showInactivePopover && (
        <SingleVariablePopover
          multiline
          anchorEl={variablePopover?.anchor ?? null}
          resolveAnchorEl={resolveVariablePopoverAnchor}
          anchorRect={variablePopover?.rect ?? null}
          variableName={variablePopover?.name ?? null}
          valueType={variablePopover?.valueType ?? "text"}
          onClose={() => setVariablePopover(null)}
        />
      )}
      {datePopover && (
        <DateVariablePopover
          anchorEl={datePopover.anchor}
          resolveAnchorEl={resolveDatePopoverAnchor}
          anchorRect={datePopover.rect}
          token={datePopover.token}
          onClose={() => setDatePopover(null)}
          onSave={handleDateTokenSave}
        />
      )}
    </>
  );
};
